<template>
    <div class="bar-widget" />
</template>

<script>
import $ from 'jquery'; //Assume kendoChart plugin is registered

//kendo.dataviz.ui.themes.default.chart.seriesColors
//When possible, we want to avoid directly invoking/depending on kendo
//thus we'll just declare their default palette as a variable
const DEFAULT_PALETTE = [ "#ff6800", "#a0a700", "#ff8d00", "#678900", "#ffb53c", "#396000" ];

//Percentages were present in the mockups in multiple formats:
// * Hidden
// * In the bar below the displayed value
// * In a tooltip bubble
const PERCENTAGE_DISPLAY_MODE = {
    HIDE: 'hide',
    UNDER_VALUE: 'belowValue',
    TOOLTIP: 'tooltip'
};

function renderPercentageUnderValue(args){
    const chart = args.sender;

    //Adding our percentage labels post render is the easiest way to add actual centered labels
    //By default, any text in our `template` cannot contain spaces
    //spacing is to be done by manipulating the dy/dx attributes of SVG nodes
    //To do that correctly, we'd need to know the available space/margins, etc.
    //that information is not available to us during render (= invocation of template function)
    //thus we'll simply copy our existing label node and change the text post render
    chart.element.find('text:has([data-percentage-label])').each((_, labelContainerNode) => {
        //Passing 'true' to 'cloneNode' creates a deep clone
        const labelContainerNodeClone = labelContainerNode.cloneNode(true);
        const labelNodeClone = labelContainerNodeClone.querySelector('[data-percentage-label]');

        //Hack: use textContent instead of innerText, our element is not located where it is rendered
        //causing our innerText to not show up
        labelNodeClone.textContent = `${labelNodeClone.dataset.percentageLabel}%`;
        labelNodeClone.removeAttribute('data-percentage-label');

        //Move our clone slightly below our original node
        labelContainerNodeClone.setAttribute(
            'y',
            parseInt(labelContainerNodeClone.attributes.y.value) + 20
        );

        labelContainerNode.parentElement.appendChild(labelContainerNodeClone);
    });
}

/**
 * Kendo's rotate function may cause long labels to stray off center a bit too for smaller screens
 * this function aims to attempt to reposition the labels to make it clear to which bar they belong 
 * @param {Object} args kendo chart render args
 */
function fixLabelPositioning(args){
    const chart = args.sender;
    
    chart.element.find('g[transform]').each(function() {
        const {width} = this.getBoundingClientRect();
        
        //https://developer.mozilla.org/en-US/docs/Web/CSS/transform-function/matrix()
        //Increase our translate X to align our label with its associated value in the bar
        this.transform.baseVal[0].matrix.e += width / 4;
    });
}

/**
 * The top of a bar widget SVG may be rendered a few pixels below the top of the parent element
 * when this happens, the SVG will overlap with any elements below it
 * by increasing the height of the parent element, the SVG will be fully contained again
 * 
 * Fixing label positioning may cause some labels to have the right part be rendered off screen
 * to combat this, a static width can be added to the element
 * @param {HTMLElement} el
 */
function scaleWithSvg(el){
    const svg = el.querySelector('svg');
    
    const { top: elTop, width: elWidth, height: elHeight } = el.getBoundingClientRect();
    const { top: svgTop, width: svgWidth } = svg.getBoundingClientRect();
    
    //After repositioning our labels, our SVG may contain children slightly wider than itself
    const actualSvgWidth = [...svg.children].reduce(
        (width, el) => Math.max(width, el.getBoundingClientRect().width),
        svgWidth
    );

    el.style.height = `${Math.ceil(elHeight + (svgTop - elTop))}px`;
    el.style.width = `${Math.ceil(Math.max(actualSvgWidth, elWidth))}px`;
}

export default {
    name: "BarGraph",
    async mounted() {
        if(!$.fn.kendoChart){
            console.error('Failed to render BarGraphWidget - kendoChart jQuery plugin not registered');
            return;
        }
        
        const $widget = $(this.$el);
        const {data, metadata} = this.data;
        
        const percentageDisplayMode = this.extractPercentageDisplayMode(metadata);
        
        $widget.kendoChart({
            series: [{
                data: data,
                field: metadata.valueColumn,
                //Cycling colors
                color: args => DEFAULT_PALETTE[args.index % DEFAULT_PALETTE.length],
                labels: {
                    visible: true,
                    background: '',
                    position: 'center',
                    template: function(args){
                        //Our returned value is rendered within an SVG <tspan> element
                        const valueSpan = document.createElement('tspan');
                        valueSpan.innerText = args.value;
                        
                        //Records with a value of 0 should not have their percentage shown
                        //the percentage is meaningless/would be rendered over the label
                        if(
                            percentageDisplayMode === PERCENTAGE_DISPLAY_MODE.UNDER_VALUE &&
                            args.value != 0 //Values may be either strings or numbers
                        ) {
                            valueSpan.setAttribute(
                                'data-percentage-label',
                                args.dataItem[metadata.percentageColumn]
                            );   
                        }
                        
                        return valueSpan.outerHTML;
                    }
                },
                tooltip: {
                    visible: percentageDisplayMode === PERCENTAGE_DISPLAY_MODE.TOOLTIP,
                    template: args => `${args.dataItem[metadata.percentageColumn]}%`,
                    background: '#FFFFFF',
                    color: '#A3A3A3',
                    border: {
                        color: '#A3A3A3'
                    }
                }
            }],
            categoryAxis: {
                categories: data.map(d => d[metadata.categoryColumn]),
                labels: {
                    rotation: 45
                }
            },
            render: args => {
                if(percentageDisplayMode === PERCENTAGE_DISPLAY_MODE.UNDER_VALUE){
                    renderPercentageUnderValue(args);
                }

                fixLabelPositioning(args);
                scaleWithSvg(this.$el);
            }
        });
    },
    methods: {
        extractPercentageDisplayMode(metadata){
            const percentageDisplayMode = metadata.percentageDisplayMode;
            const validValues = Object.values(PERCENTAGE_DISPLAY_MODE);
            
            if(validValues.includes(percentageDisplayMode)){
                return percentageDisplayMode;
            }
            
            //Hidden by default; a percentage should not be required
            return PERCENTAGE_DISPLAY_MODE.HIDE;
        }
    },
    props: {
        /**
         * @type {SankeyTouchpointInfoConfigWidget}
         */
        configuration: {
            type: Object,
            required: true
        },
        data: {
            type: Object,
            required: true
        }
    }
}
</script>
